'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { SettingsValueProps } from './types';
import { SettingsContext } from './settings-context';

// ----------------------------------------------------------------------

export const STORAGE_KEY_THEME = 'theme-settings';

function readStoredThemeMode(): Partial<Pick<SettingsValueProps, 'themeMode'>> {
  try {
    const { themeMode } = JSON.parse(localStorage.getItem(STORAGE_KEY_THEME) ?? '{}');
    return themeMode === 'light' || themeMode === 'dark' ? { themeMode } : {};
  } catch {
    return {};
  }
}

type SettingsProviderProps = {
  children: React.ReactNode;
  defaultSettings: SettingsValueProps;
};

export function SettingsProvider({ children, defaultSettings }: SettingsProviderProps) {
  const [state, setState] = useState<SettingsValueProps>(() => ({
    ...defaultSettings,
    ...readStoredThemeMode(),
  }));

  useEffect(() => {
    document.documentElement.dataset.themeMode = state.themeMode;
    try {
      localStorage.setItem(STORAGE_KEY_THEME, JSON.stringify({ themeMode: state.themeMode }));
    } catch {
      // Storage unavailable (private mode, blocked site data): the choice lasts for this session.
    }
  }, [state.themeMode]);

  const update = useCallback(
    <K extends keyof SettingsValueProps>(name: K, updatedValue: SettingsValueProps[K]) => {
      setState((prevState) => ({ ...prevState, [name]: updatedValue }));
    },
    []
  );

  const reset = useCallback(() => setState(defaultSettings), [defaultSettings]);

  const memoizedValue = useMemo(
    () => ({
      ...state,
      onUpdate: update,
      onReset: reset,
    }),
    [reset, update, state]
  );

  return <SettingsContext.Provider value={memoizedValue}>{children}</SettingsContext.Provider>;
}
